import { Injectable } from '@angular/core';

import { Fixture, FixtureStage, Game, Option, OptionMarket, OptionMarketStatus, OptionStatus, Result, Visibility } from '@cds/betting-offer';
import { Competition, Region, Sport } from '@cds/betting-offer/tags';
import { Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { OddsOperations } from '@frontend/sports/odds/feature';

import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { toComboPrevention } from '../../../core/picks/combo-prevention';
import { toMinimumCombo, toMinimumComboTV2 } from '../../../core/picks/minimum-combination';
import { V1PickId } from '../../../core/picks/pick-id';
import {
    BetslipV1PickEvent,
    BetslipV1PickLeague,
    BetslipV1PickMarket,
    BetslipV1PickOption,
    BetslipV1PickRegion,
    BetslipV1PickSport,
    BetslipV1PickTeaserData,
    PriceType,
} from '../../../core/picks/pick-models';
import { createSignedName } from '../../../core/picks/signed-name.model';
import { optionPriceToBetslipPrice } from '../../../core/price';
import { getEventParticipants } from './factory.helper';

@Injectable()
export default class V1PickFactory {
    create(pickId: V1PickId, fixture: Fixture, isPriceBoosted?: boolean): BetslipV1Pick | null {
        if (fixture.id !== pickId.eventId) {
            return null;
        }
        const pick = new BetslipV1Pick();
        pick.sport = this.createPickSport(fixture.sport);
        pick.league = this.createPickLeague(fixture.competition);
        pick.region = this.createPickRegion(fixture.region);
        pick.event = this.createPickEvent(fixture);
        pick.teaser = this.createPickTeaserData(fixture);

        if (fixture.hybridFixtureData != null) {
            const optionMarket = fixture.optionMarkets.find((g) => g.id === pickId.marketId);
            if (!optionMarket) {
                return null;
            }
            const option = optionMarket.options.find((o) => o.id === pickId.optionId);
            if (!option) {
                return null;
            }
            pick.market = this.createPickMarketFromOptionMarket(optionMarket);

            pick.option = this.createPickOptionFromOption(optionMarket, option);

            if (isPriceBoosted && option.boostedPrice) {
                pick.boostedPriceDetails = {
                    boostedPrice: optionPriceToBetslipPrice(optionMarket, option.boostedPrice),
                    boostedPriceId: option.boostedPrice.id,
                    isPriceBoosted,
                };
            }

            pick.acceptedPrice = {
                price: pick.currentPrice ? { ...pick.currentPrice } : null,
                isManuallyAccepted: false,
            };
            pick.id = pickId;

            pick.totalsPrefix = optionMarket.options.find((x) => x.id === pickId.optionId)?.totalsPrefix;
            pick.fromHybridFixture = true;
        } else {
            const game = fixture.games.find((g) => g.id === pickId.marketId);
            if (!game) {
                return null;
            }
            const result = game.results.find((o) => o.id === pickId.optionId);
            if (!result) {
                return null;
            }
            pick.market = this.createPickMarket(game);
            pick.option = this.createPickOption(game, result);
            pick.acceptedPrice = {
                price: { ...pick.currentPrice! },
                isManuallyAccepted: false,
            };
            pick.id = pickId;

            pick.totalsPrefix = game.results.find((x) => x.id === pickId.optionId)?.totalsPrefix;
            pick.fromHybridFixture = false;
        }

        return pick;
    }

    private createPickSport(fixtureSport: Sport): BetslipV1PickSport {
        return {
            name: createSignedName(fixtureSport.name),
            id: fixtureSport.id,
        };
    }

    private createPickTeaserData(input: Fixture): BetslipV1PickTeaserData {
        return {
            crossSportIds: input.teaserbet?.crossSportIds,
            crossLeagueIds: input.teaserbet?.crossLeagueIds,
        };
    }

    private createPickEvent(input: Fixture): BetslipV1PickEvent {
        return {
            id: input.id,
            name: createSignedName(input.name),
            groupId: input.groupId || null,
            eventDate: new Date(input.startDate),
            cutOffDate: new Date(input.cutOffDate),
            isLive: input.stage === FixtureStage.Live,
            isPublished: true,
            participants: getEventParticipants(input),
            viewType: input.viewType,
        };
    }

    private createPickRegion(input: Region): BetslipV1PickRegion {
        return {
            name: createSignedName(input.name),
            id: input.id,
        };
    }

    private createPickLeague(input: Competition): BetslipV1PickLeague {
        return {
            name: createSignedName(input.name),
            id: input.id,
            parentLeagueId: typeof input.parentLeagueId === 'undefined' ? null : input.parentLeagueId,
        };
    }

    private createPickMarket(game: Game): BetslipV1PickMarket {
        return {
            id: game.id,
            name: createSignedName(game.name),
            templateId: game.templateId,
            isVisible: game.visibility === Visibility.Visible,
            isClosed: game.visibility === Visibility.Hidden,
            comboPrevention: toComboPrevention(game.combo1),
            minimumCombo: toMinimumCombo(game.combo2),
            gridGroups: game.grouping?.gridGroups,
            templateCategoryId: game.categoryId,
            isBetBuilderEnabled: !!game.isBetBuilder,
        };
    }

    private createPickMarketFromOptionMarket(optionMarket: OptionMarket): BetslipV1PickMarket {
        return {
            id: optionMarket.id,
            isVisible: optionMarket.status === OptionMarketStatus.Visible,
            isClosed: optionMarket.status === OptionMarketStatus.Hidden,
            name: createSignedName(optionMarket.name),
            comboPrevention: toComboPrevention(optionMarket.comboPrevention),
            minimumCombo: toMinimumComboTV2(optionMarket.minCombo),
            gridGroups: optionMarket.grouping?.gridGroups,
            templateCategoryId: optionMarket.templateCategory.id,
            templateId: Number(optionMarket.parameters.find((f) => f.key === Tv2MarketParameters.TemplateId)?.value),
            isBetBuilderEnabled: !!optionMarket.isBetBuilder,
        };
    }

    private createPickOption(game: Game, result: Result): BetslipV1PickOption {
        return {
            id: result.id,
            name: createSignedName(result.name),
            price: {
                id: result.id,
                isVisible: result.visibility === Visibility.Visible,
                marketId: game.id,
                type: PriceType.Fixed,
                nativeOdds: OddsOperations.createOdds(result),
            },
        };
    }

    private createPickOptionFromOption(optionMarket: OptionMarket, option: Option): BetslipV1PickOption {
        return {
            id: option.id,
            name: createSignedName(option.name),
            price: {
                id: option.id,
                isVisible: !!(option.status === OptionStatus.Visible && option.price.odds && option.price.odds > 1),
                marketId: optionMarket.id,
                type: PriceType.Fixed,
                nativeOdds: OddsOperations.createOdds(option.price),
            },
        };
    }
}
